import { Box, Col, Row } from '@opendoor/atoms/box';
import { Image } from '@opendoor/atoms/image';
import { Text } from '@opendoor/atoms/text';
import type { EntryComponent } from 'cms/entries/entries';
import type { ILpComponentPartnerCaseStudy } from 'declarations/contentful';
import { optimizeContentfulImageUrl } from 'helpers/imageUrl';

import Container from './shared/Container';

const getImageUrl = (entry: ILpComponentPartnerCaseStudy) => {
  const imageUrl = entry.fields.partnerLogo?.fields?.file?.url;

  return imageUrl ? optimizeContentfulImageUrl(imageUrl, { width: 160, quality: 80 }) : undefined;
};

const getImageAlt = (entry: ILpComponentPartnerCaseStudy) => {
  const { partnerLogo, quoteAuthor, partnerName } = entry.fields;
  return (
    partnerLogo?.fields?.description ||
    partnerLogo?.fields?.title ||
    quoteAuthor ||
    partnerName ||
    ''
  );
};

const CaseStudyImage = ({ imageUrl, alt }: { imageUrl: string; alt: string }) => {
  return (
    <Image
      src={imageUrl}
      alt={alt}
      className="block h-10 w-10 shrink-0 rounded-full object-cover"
    />
  );
};

export const RenderPartnerCaseStudy = (entry: ILpComponentPartnerCaseStudy) => {
  const { fields } = entry;
  const { quote, quoteAuthor, quoteRole } = fields;
  const imageUrl = getImageUrl(entry);
  const hasAttribution = quoteAuthor || quoteRole;

  if (!quote && !hasAttribution) {
    return null;
  }

  return (
    <Container noSectionSpacing>
      <Col className="items-center">
        <Col
          analyticsName={`cosmos-landing-page-partner-case-study-${
            fields.analyticsId ?? entry.sys?.id ?? fields.internalName
          }`}
          trackImpression
          className="w-full max-w-192 gap-4 rounded-2xl border border-od-border-primary bg-od-bg-tertiary p-5 md:p-8"
        >
          {quote && (
            <Text
              as="p"
              className="block text-body-lg font-medium text-od-content-primary md:text-subheader-xs"
            >
              &ldquo;{quote}&rdquo;
            </Text>
          )}

          {hasAttribution && (
            <Row className="items-center gap-3 md:gap-4">
              {imageUrl ? (
                <CaseStudyImage imageUrl={imageUrl} alt={getImageAlt(entry)} />
              ) : (
                <Box className="h-10 w-10 shrink-0 items-center justify-center rounded-full border border-od-border-primary bg-od-bg-primary">
                  <Text as="span" className="text-xs text-od-content-tertiary">
                    (photo)
                  </Text>
                </Box>
              )}
              <Col className="gap-0.5">
                {quoteAuthor && (
                  <Text as="p" className="block text-label-lg text-od-content-primary">
                    {quoteAuthor}
                  </Text>
                )}
                {quoteRole && (
                  <Text as="p" className="block text-body-sm text-od-content-tertiary">
                    {quoteRole}
                  </Text>
                )}
              </Col>
            </Row>
          )}
        </Col>
      </Col>
    </Container>
  );
};

const PartnerCaseStudy: EntryComponent<ILpComponentPartnerCaseStudy> = {
  render: RenderPartnerCaseStudy,
};

export default PartnerCaseStudy;
